import { Link, getRouteApi } from '@tanstack/react-router';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { AccountError } from '../account/api.ts';
import {
  SHARE_ROLES,
  changeRole,
  invite,
  lookUp,
  members,
  peopleOfRealm,
  READING_ROLES,
  rekeyDue,
  removeMember,
  roleLabel,
  sealEditionKeys,
  updateCoOwnedCopies,
  updateCopies,
  type Member,
  type PeopleOfRealm,
  type ShareRole,
  type Shared,
} from '../account/sharing.ts';
import { FingerprintCheck, useChecks, type Checks } from './FingerprintCheck.tsx';
import { RealmClientError } from '../db/client.ts';
import { realmFile, type SharedCopy } from '../db/protocol.ts';
import { formatDate } from '../files.ts';
import { PreviewButtons } from './PreviewCopy.tsx';
import { SharingOverviewLink } from './SharingOverview.tsx';

const route = getRouteApi('/_app/realm/$realmKey');
const sharingRoute = getRouteApi('/_app/realm/$realmKey/sharing');

function message(error: unknown): string {
  return error instanceof AccountError || error instanceof RealmClientError ? error.message : 'That didn’t work. Please try again.';
}

/**
 * Sharing… (docs/SHARING-PLAN.md sections 3.3 and 3.4): who the realm is shared with,
 * each in a role, Invite, and Update players' copy, which makes what they read again from
 * the realm as it is now.
 */
export function SharingPage() {
  const { realm, shared } = route.useLoaderData();
  const { realmKey } = route.useParams();
  const { sent } = sharingRoute.useSearch();
  const file = realmFile(realmKey);
  const [list, setList] = useState<Member[] | 'not-backed-up' | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [problem, setProblem] = useState('');
  const [notice, setNotice] = useState(sent ? 'Updated. Players and readers get it the next time they open the realm.' : '');
  const [busy, setBusy] = useState(false);

  const checks = useChecks();
  const load = useCallback(async () => {
    try {
      const found = await members(realmKey);
      setList('notBackedUp' in found ? 'not-backed-up' : found.members);
      setUpdatedAt('notBackedUp' in found ? null : found.updatedAt);
      setProblem('');
      // Someone left (or was removed from another device) while others still read their copy, or a
      // co-owner went: a new key now.
      if (!('notBackedUp' in found) && (found.rekeyDue || found.realmRekeyDue)) {
        setBusy(true);
        try {
          await rekeyDue({ file, realmId: realmKey, realmName: realm.name, people: found.members }, found.realmRekeyDue);
          setNotice(
            found.realmRekeyDue
              ? 'A co-owner left, so the realm is now locked with a new key: they can’t read anything written from now on.'
              : 'Someone left, so their copy was made again under a new key: they get nothing you write from now on.',
          );
        } finally {
          setBusy(false);
        }
      }
      // Each copy's key sealed for co-owners, where it isn't yet (SHARING-CO-OWNERS-PLAN.md section 7).
      if (!('notBackedUp' in found)) {
        void sealEditionKeys({ file, realmId: realmKey, realmName: realm.name, people: found.members }).catch((error: unknown) =>
          console.warn('Could not seal the copies’ keys for co-owners', error),
        );
      }
    } catch (error) {
      setProblem(message(error));
    }
  }, [realmKey, file, realm.name]);
  useEffect(() => {
    if (!shared) void load();
  }, [load, shared]);

  async function run(work: () => Promise<string>) {
    setProblem('');
    setNotice('');
    setBusy(true);
    try {
      setNotice(await work());
      await load();
    } catch (error) {
      setProblem(message(error));
    } finally {
      setBusy(false);
    }
  }

  if (shared?.previewOf) {
    return (
      <article className="document sharing-page">
        <h1>Sharing</h1>
        <p className="callout">This is a preview of a copy. Close the preview to share the realm.</p>
        <SharingOverviewLink />
      </article>
    );
  }
  if (shared?.role === 'editor') return <CoOwnerSharing shared={shared} />;
  if (shared) {
    return (
      <article className="document sharing-page">
        <h1>Sharing</h1>
        <p className="callout">Only its owner, {shared.ownerEmail}, can share this realm.</p>
        <SharingOverviewLink />
      </article>
    );
  }

  return (
    <article className="document sharing-page">
      <p className="eyebrow">
        <Link to="/realm/$realmKey" params={{ realmKey }}>
          {realm.name}
        </Link>
      </p>
      <h1>Sharing</h1>
      <p className="lead">
        Share this realm with a co-owner, who changes it with you, or with your players and readers. Each player or reader sees
        only what their audience may: a player never gets GM-only notes, pins or regions, not even in encrypted form. Realmery
        can&apos;t read what you share.
      </p>
      <p className="meta">
        What they read is a copy made when you invite them, or press Update players&apos; copy: your changes reach them only when
        you choose, so you can prepare a session in peace.
      </p>
      <SharingOverviewLink />
      <div className="actions" aria-label="Preview a copy" role="group">
        <PreviewButtons file={file} />
      </div>

      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {problem && (
        <p className="notice notice-error" role="alert">
          {problem}
        </p>
      )}

      {list === null && !problem && <p className="meta">Loading…</p>}
      {list === 'not-backed-up' && (
        <p className="callout">
          This realm isn&apos;t backed up to your account yet, and sharing goes through its backup. Wait for it to back up, or turn
          backing up on for it under Backup… in your realm list.
        </p>
      )}
      {Array.isArray(list) && (() => {
        const shared: Shared = { file, realmId: realmKey, realmName: realm.name, people: list };
        return <>
          <section className="panel" aria-labelledby="people-heading">
            <h2 id="people-heading">People</h2>
            {list.length === 0 ? (
              <p className="meta">Not shared with anyone yet.</p>
            ) : (
              <ul className="member-list">
                {list.map((m) => (
                  <MemberRow key={m.userId} member={m} shared={shared} checks={checks} busy={busy} onRun={run} />
                ))}
              </ul>
            )}
            {list.some((m) => m.role !== 'editor') && (
              <div className="copy-update">
                <p className="meta">{updatedAt === null ? 'No copy made yet.' : `Their copy was last updated ${formatDate(updatedAt)}.`}</p>
                <button
                  type="button"
                  className="primary"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await updateCopies(shared);
                      return 'Updated. Players and readers get it the next time they open the realm.';
                    })
                  }
                >
                  {busy ? 'Working…' : 'Update players’ copy'}
                </button>
              </div>
            )}
          </section>
          <InviteForm shared={shared} busy={busy} onRun={run} />
        </>;
      })()}
    </article>
  );
}

/** "the players’ copy", "the players’ and readers’ copies" */
function copiesOf(roles: readonly string[]): string {
  const names = READING_ROLES.filter((r) => roles.includes(r.value)).map((r) => `${r.label.toLowerCase()}s’`);
  return `the ${names.join(' and ')} ${names.length > 1 ? 'copies' : 'copy'}`;
}

/** What a co-owner's Update players' copy did (SHARING-CO-OWNERS-PLAN.md section 7). */
export function coOwnerUpdateNotice(updated: readonly string[], ownerMust: readonly string[], ownerEmail: string): string {
  const done = updated.length === 0 ? '' : `Updated ${copiesOf(updated)}: they get it the next time they open the realm.`;
  const left = ownerMust.length === 0 ? '' : `Only ${ownerEmail} can update ${copiesOf(ownerMust)} just now: ask them to press Update players’ copy once.`;
  return [done, left].filter(Boolean).join(' ') || 'No one reads a copy of this realm yet.';
}

/**
 * A co-owner's Sharing page (SHARING-CO-OWNERS-PLAN.md sections 3.1 and 7): who the realm
 * is shared with, to see, not change, and Update players' copy.
 */
function CoOwnerSharing({ shared }: { shared: SharedCopy }) {
  const [people, setPeople] = useState<PeopleOfRealm | null>(null);
  const [problem, setProblem] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [loads, setLoads] = useState(0);
  useEffect(() => {
    let live = true;
    peopleOfRealm(shared.ownerId, shared.realmId).then(
      (found) => live && setPeople(found),
      (error: unknown) => live && setProblem(message(error)),
    );
    return () => {
      live = false;
    };
  }, [shared.ownerId, shared.realmId, loads]);
  const readers = people?.members.filter((m) => m.role !== 'editor') ?? [];
  const updatedAt = people && people.editions.length > 0 ? Math.min(...people.editions.map((e) => e.updatedAt)) : null;
  async function update() {
    setBusy(true);
    setNotice('');
    setProblem('');
    try {
      const { updated, ownerMust } = await updateCoOwnedCopies(shared);
      setNotice(coOwnerUpdateNotice(updated, ownerMust, shared.ownerEmail));
      setLoads((n) => n + 1);
    } catch (error) {
      setProblem(message(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <article className="document sharing-page">
      <h1>Sharing</h1>
      <p className="callout">Only its owner, {shared.ownerEmail}, can share this realm, or change who it’s shared with.</p>
      <SharingOverviewLink />
      {problem && (
        <p className="notice notice-error" role="alert">
          {problem}
        </p>
      )}
      {!people && !problem && <p className="meta">Loading…</p>}
      {people && (
        <section className="panel" aria-labelledby="people-heading">
          <h2 id="people-heading">People</h2>
          <ul className="member-list">
            <li className="member" aria-label={people.owner.email}>
              <strong>{people.owner.email}</strong> <span className="chip">Owner</span>
            </li>
            {people.members.map((m) => (
              <li key={m.email} className="member" aria-label={m.email}>
                <strong>{m.email}</strong>
                {m.you && ' (you)'} <span className="chip">{roleLabel(m.role)}</span>
                {m.status === 'invited' && <span className="meta"> · invited</span>}
              </li>
            ))}
          </ul>
          {readers.length > 0 && (
            <div className="copy-update">
              <p className="meta">{updatedAt === null ? 'No copy made yet.' : `Their copy was last updated ${formatDate(updatedAt)}.`}</p>
              <button type="button" className="primary" disabled={busy} onClick={() => void update()}>
                {busy ? 'Working…' : 'Update players’ copy'}
              </button>
            </div>
          )}
        </section>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
    </article>
  );
}

type Run = (work: () => Promise<string>) => Promise<void>;

function MemberRow({ member, shared, checks, busy, onRun }: { member: Member; shared: Shared; checks: Checks; busy: boolean; onRun: Run }) {
  const [confirming, setConfirming] = useState(false);
  const invited = member.status === 'invited';
  return (
    <li className="member" aria-label={member.email}>
      <div>
        <strong>{member.email}</strong>{' '}
        <span className="chip">{invited ? `Invited ${formatDate(member.invitedAt)}` : 'Member'}</span>
        <FingerprintCheck userId={member.userId} fingerprint={member.fingerprint} checks={checks} />
      </div>
      <div className="actions">
        <label>
          Role{' '}
          <select
            value={member.role}
            disabled={busy}
            onChange={(e) => {
              const role = e.currentTarget.value as ShareRole;
              void onRun(async () => {
                await changeRole(shared, member, role);
                return `${member.email} is now a ${roleLabel(role)}.`;
              });
            }}
          >
            {SHARE_ROLES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        {confirming ? (
          <>
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() =>
                void onRun(async () => {
                  await removeMember(shared, member);
                  return invited
                    ? `Withdrew the invitation to ${member.email}.`
                    : `Removed ${member.email}. They get nothing you share from now on.`;
                })
              }
            >
              {invited ? 'Withdraw' : 'Remove'}
            </button>
            <button type="button" className="quiet" onClick={() => setConfirming(false)}>
              Keep
            </button>
          </>
        ) : (
          <button type="button" className="quiet" onClick={() => setConfirming(true)}>
            {invited ? 'Withdraw invitation…' : 'Remove…'}
          </button>
        )}
      </div>
    </li>
  );
}

function InviteForm({ shared, busy, onRun }: { shared: Shared; busy: boolean; onRun: Run }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ShareRole>('player');
  function submit(event: FormEvent) {
    event.preventDefault();
    void onRun(async () => {
      const found = await lookUp(email);
      await invite(shared, found, role);
      setEmail('');
      return `Invited ${found.email} as a ${roleLabel(role)}. They'll see the invitation when they next sign in.`;
    });
  }
  return (
    <form className="panel" aria-labelledby="invite-heading" onSubmit={submit}>
      <h2 id="invite-heading">Invite someone</h2>
      <p className="meta">They need a Realmery account. Up to 10 people a realm.</p>
      <label>
        Their email
        <input type="email" required autoComplete="off" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
      </label>
      <fieldset className="role-choice">
        <legend>Role</legend>
        {SHARE_ROLES.map((r) => (
          <label key={r.value} className="check">
            <input type="radio" name="role" value={r.value} checked={role === r.value} onChange={() => setRole(r.value)} />
            <span>
              <strong>{r.label}</strong>: {r.description}
            </span>
          </label>
        ))}
      </fieldset>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>
          Invite
        </button>
      </div>
    </form>
  );
}
